import AsyncStorage from "@react-native-async-storage/async-storage";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Pressable, ScrollView, Text, View } from "react-native";
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  CreditCard,
  Download,
  Edit3,
  Eye,
  FileText,
  Plus,
  Receipt,
  RotateCcw,
  Search,
  Send,
  Trash2,
  X,
  XCircle
} from "lucide-react-native";
import { api } from "../api/client";
import { formatAmount, formatCurrency, isAdmin as checkAdmin, isManager as checkManager } from "../utils/format";
import { ActionMenu, Badge, Button, Card, DataTable, Field, IconButton, Modal, ScreenHeader, Select, StatCard, Toolbar } from "../components/UI";
import { colors, styles } from "../styles";

const today = () => new Date().toISOString().slice(0, 10);

const PAYMENT_METHODS = [
  "Bank Transfer",
  "Cheque",
  "Cash",
  "Online / Raast",
  "Pay Order",
  "Credit Card",
  "Other"
];

export function InvoicesScreen({ user, navigate, initialInvoiceId = null, initialQuotationId = null }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState("");

  // Modals & Active states
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [approvedQuotations, setApprovedQuotations] = useState([]);
  const [loadingQuotations, setLoadingQuotations] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentTargetInvoice, setPaymentTargetInvoice] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTargetInvoice, setCancelTargetInvoice] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [showEditDraftModal, setShowEditDraftModal] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);

  // Payment Form state
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_date: today(),
    payment_method: "Bank Transfer",
    transaction_reference: "",
    bank_details: "",
    notes: ""
  });
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);

  // New Invoice Form state for modal
  const [selectedQuotationId, setSelectedQuotationId] = useState("");
  const [createForm, setCreateForm] = useState({
    invoice_date: today(),
    due_date: "",
    payment_terms: "",
    remarks: "",
    invoice_type: "commercial",
    tax_rate: "18"
  });
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  const isAdmin = checkAdmin(user);
  const isManager = checkManager(user);
  const canManage = isAdmin || isManager;

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const rows = await api.invoices(q, dateFrom, dateTo, status, paymentFilter, invoiceTypeFilter);
      setInvoices(rows || []);
    } catch (error) {
      Alert.alert("Invoices", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  useEffect(() => {
    if (initialInvoiceId) {
      openInvoiceDetail(initialInvoiceId);
    }
  }, [initialInvoiceId]);

  useEffect(() => {
    if (initialQuotationId) {
      openCreateModal(initialQuotationId);
    }
  }, [initialQuotationId]);

  // Statistics
  const stats = useMemo(() => {
    let totalInvoiced = 0;
    let totalPaid = 0;
    let totalOutstanding = 0;
    let draftCount = 0;
    let issuedCount = 0;
    let paidCount = 0;
    let overdueCount = 0;

    invoices.forEach((inv) => {
      const grand = Number(inv.grand_total || 0);
      const paid = Number(inv.amount_paid || 0);
      const out = Number(inv.outstanding_amount || 0);
      const st = String(inv.status || "draft").toLowerCase();

      if (st !== "cancelled") {
        totalInvoiced += grand;
        totalPaid += paid;
        totalOutstanding += out;
      }

      if (st === "draft") draftCount += 1;
      else if (st === "issued" || st === "partially_paid") issuedCount += 1;
      else if (st === "paid") paidCount += 1;
      else if (st === "overdue") overdueCount += 1;
    });

    return {
      totalCount: invoices.length,
      totalInvoiced,
      totalPaid,
      totalOutstanding,
      draftCount,
      issuedCount,
      paidCount,
      overdueCount
    };
  }, [invoices]);

  const openInvoiceDetail = async (id) => {
    try {
      setDetailLoading(true);
      const invoice = await api.invoice(id);
      setSelectedInvoice(invoice);
    } catch (error) {
      Alert.alert("Invoice Details", error.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadPdf = async (id, type = "final") => {
    const token = await AsyncStorage.getItem("auth_token");
    if (typeof window !== "undefined") {
      window.open(`${api.invoicePdfUrl(id)}?type=${type}&token=${token}`, "_blank");
    }
  };

  const handleIssueInvoice = async (invoiceId) => {
    if (!confirm("Are you sure you want to officially issue this invoice? Once issued, items cannot be edited without cancellation.")) {
      return;
    }
    try {
      await api.issueInvoice(invoiceId);
      Alert.alert("Success", "Invoice issued successfully.");
      loadInvoices();
      if (selectedInvoice && selectedInvoice.id === invoiceId) {
        openInvoiceDetail(invoiceId);
      }
    } catch (error) {
      Alert.alert("Issue Invoice", error.message);
    }
  };

  const openPaymentModal = (invoice) => {
    setPaymentTargetInvoice(invoice);
    const balance = Math.max(0, Number(invoice.outstanding_amount || 0));
    setPaymentForm({
      amount: String(balance),
      payment_date: today(),
      payment_method: "Bank Transfer",
      transaction_reference: "",
      bank_details: "",
      notes: ""
    });
    setShowPaymentModal(true);
  };

  const handleRecordPayment = async () => {
    if (!paymentTargetInvoice) return;
    const amountNum = Number(paymentForm.amount);
    if (!amountNum || amountNum <= 0) {
      Alert.alert("Validation", "Please enter a valid payment amount greater than zero.");
      return;
    }
    if (amountNum > Number(paymentTargetInvoice.outstanding_amount) + 0.01) {
      Alert.alert("Validation", `Payment amount cannot exceed outstanding balance (${formatCurrency(paymentTargetInvoice.outstanding_amount)}).`);
      return;
    }

    try {
      setPaymentSubmitting(true);
      await api.recordInvoicePayment(paymentTargetInvoice.id, {
        amount: amountNum,
        payment_date: paymentForm.payment_date || today(),
        payment_method: paymentForm.payment_method,
        transaction_reference: paymentForm.transaction_reference,
        bank_details: paymentForm.bank_details,
        notes: paymentForm.notes
      });
      Alert.alert("Success", "Payment recorded successfully.");
      setShowPaymentModal(false);
      loadInvoices();
      if (selectedInvoice && selectedInvoice.id === paymentTargetInvoice.id) {
        openInvoiceDetail(paymentTargetInvoice.id);
      }
    } catch (error) {
      Alert.alert("Record Payment", error.message);
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const openCancelModal = (invoice) => {
    setCancelTargetInvoice(invoice);
    setCancelReason("");
    setShowCancelModal(true);
  };

  const handleCancelInvoice = async () => {
    if (!cancelTargetInvoice) return;
    if (!cancelReason.trim()) {
      Alert.alert("Validation", "Please provide a reason for cancelling this invoice.");
      return;
    }
    try {
      await api.cancelInvoice(cancelTargetInvoice.id, cancelReason.trim());
      Alert.alert("Success", "Invoice cancelled successfully.");
      setShowCancelModal(false);
      loadInvoices();
      if (selectedInvoice && selectedInvoice.id === cancelTargetInvoice.id) {
        openInvoiceDetail(cancelTargetInvoice.id);
      }
    } catch (error) {
      Alert.alert("Cancel Invoice", error.message);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!confirm("Are you sure you want to permanently delete this draft invoice?")) {
      return;
    }
    try {
      await api.deleteInvoice(invoiceId);
      Alert.alert("Success", "Draft invoice deleted.");
      if (selectedInvoice && selectedInvoice.id === invoiceId) {
        setSelectedInvoice(null);
      }
      loadInvoices();
    } catch (error) {
      Alert.alert("Delete Invoice", error.message);
    }
  };

  // Create Invoice Modal
  const openCreateModal = async (preselectedQuotationId = null) => {
    setShowCreateModal(true);
    const initialQid = preselectedQuotationId ? String(preselectedQuotationId) : "";
    setSelectedQuotationId(initialQid);
    setCreateForm({
      invoice_date: today(),
      due_date: "",
      payment_terms: "30 Days Net",
      remarks: "",
      invoice_type: "commercial",
      tax_rate: "18"
    });
    try {
      setLoadingQuotations(true);
      const rows = await api.quotations("", "", "", "approved");
      setApprovedQuotations(rows || []);
      if (initialQid) {
        const match = (rows || []).find((q) => String(q.id) === initialQid);
        if (match) {
          const hasTax = Number(match.tax_total || 0) > 0;
          setCreateForm((prev) => ({
            ...prev,
            invoice_type: hasTax ? "sales_tax" : "commercial",
            payment_terms: match.terms || prev.payment_terms || "30 Days Net",
            remarks: match.remarks || prev.remarks
          }));
        }
      }
    } catch (error) {
      Alert.alert("Approved Quotations", error.message);
    } finally {
      setLoadingQuotations(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!selectedQuotationId) {
      Alert.alert("Validation", "Please select an approved quotation to generate an invoice.");
      return;
    }
    try {
      setCreatingInvoice(true);
      const payload = {
        ...createForm,
        tax_rate: Number(createForm.tax_rate || 0)
      };
      const inv = await api.createInvoiceFromQuotation(selectedQuotationId, payload);
      Alert.alert("Success", `Invoice ${inv.invoice_number} created successfully.`);
      setShowCreateModal(false);
      loadInvoices();
      openInvoiceDetail(inv.id);
    } catch (error) {
      Alert.alert("Create Invoice", error.message);
    } finally {
      setCreatingInvoice(false);
    }
  };

  // Edit Draft Modal
  const openEditDraftModal = (invoice) => {
    setEditingDraft({
      ...invoice,
      invoice_type: invoice.invoice_type || "commercial",
      tax_rate: String(invoice.tax_rate != null ? invoice.tax_rate : (invoice.invoice_type === "sales_tax" ? 18 : 0)),
      items: (invoice.items || []).map((it) => ({
        ...it,
        quantity: String(it.quantity || 1),
        unit_price: String(it.unit_price || 0),
        discount: String(it.discount || 0),
        tax: String(it.tax || 0)
      }))
    });
    setShowEditDraftModal(true);
  };

  const handleSaveDraftChanges = async () => {
    if (!editingDraft) return;
    try {
      const payload = {
        invoice_date: editingDraft.invoice_date,
        due_date: editingDraft.due_date,
        payment_terms: editingDraft.payment_terms,
        project_title: editingDraft.project_title,
        invoice_type: editingDraft.invoice_type || "commercial",
        tax_rate: Number(editingDraft.tax_rate || 0),
        extra_charges: Number(editingDraft.extra_charges || 0),
        remarks: editingDraft.remarks,
        terms: editingDraft.terms,
        items: (editingDraft.items || []).map((it) => ({
          ...it,
          quantity: Number(it.quantity || 1),
          unit_price: Number(it.unit_price || 0),
          discount: Number(it.discount || 0),
          tax: Number(it.tax || 0)
        }))
      };
      const updated = await api.updateInvoice(editingDraft.id, payload);
      Alert.alert("Success", "Draft invoice updated successfully.");
      setShowEditDraftModal(false);
      loadInvoices();
      setSelectedInvoice(updated);
    } catch (error) {
      Alert.alert("Update Invoice", error.message);
    }
  };

  return (
    <View style={styles.page}>
      <ScreenHeader
        title="Invoice Module"
        subtitle="Manage tax invoices, track customer billing, record payments, and export PDF documents"
      >
        {canManage ? (
          <Button icon={Plus} onPress={openCreateModal}>
            New Invoice from Quotation
          </Button>
        ) : null}
      </ScreenHeader>

      {/* Top Stat Cards */}
      <View style={styles.row}>
        <StatCard
          label="Total Invoices"
          value={stats.totalCount}
          icon={Receipt}
          subtitle={`${stats.paidCount} paid, ${stats.draftCount} draft`}
        />
        <StatCard
          label="Total Invoiced"
          value={formatCurrency(stats.totalInvoiced)}
          icon={FileText}
          subtitle="Non-cancelled billed value"
        />
        <StatCard
          label="Total Received"
          value={formatCurrency(stats.totalPaid)}
          icon={CreditCard}
          subtitle="Collected payments"
        />
        <StatCard
          label="Outstanding Balance"
          value={formatCurrency(stats.totalOutstanding)}
          icon={AlertCircle}
          subtitle="Receivable from clients"
        />
      </View>

      {/* Filter Card */}
      <Card style={{ padding: 16 }}>
        <Toolbar>
          <Field
            label="Search Query"
            value={q}
            onChangeText={setQ}
            placeholder="Invoice No, Client, Quotation, Project..."
            style={{ flex: 2 }}
          />
          <Field
            label="Date From"
            value={dateFrom}
            onChangeText={setDateFrom}
            placeholder="YYYY-MM-DD"
          />
          <Field
            label="Date To"
            value={dateTo}
            onChangeText={setDateTo}
            placeholder="YYYY-MM-DD"
          />
          <Select label="Invoice Status" value={status} onChangeText={setStatus}>
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="issued">Issued</option>
            <option value="partially_paid">Partially Paid</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </Select>
          <Select label="Payment State" value={paymentFilter} onChangeText={setPaymentFilter}>
            <option value="">All Balances</option>
            <option value="unpaid">Outstanding Balance</option>
            <option value="paid">Fully Settled (Paid)</option>
            <option value="overdue">Overdue</option>
          </Select>
          <Select label="Invoice Type" value={invoiceTypeFilter} onChangeText={setInvoiceTypeFilter}>
            <option value="">All Types</option>
            <option value="sales_tax">Sales Tax (GST)</option>
            <option value="commercial">Commercial (No Tax)</option>
          </Select>
          <Button icon={Search} onPress={loadInvoices}>
            Filter
          </Button>
        </Toolbar>
      </Card>

      {/* Invoices DataTable */}
      <DataTable
        actionsWidth={56}
        columns={[
          {
            key: "invoice_number",
            label: "Invoice No",
            flex: 1.2,
            style: { fontWeight: "700" }
          },
          {
            key: "invoice_type",
            label: "Type",
            flex: 1,
            render: (r) => <InvoiceTypeBadge type={r.invoice_type} rate={r.tax_rate} />
          },
          { key: "client_name", label: "Client Name", flex: 1.5 },
          {
            key: "quotation_number",
            label: "Ref Quotation",
            flex: 1.1,
            render: (r) => r.quotation_number || "-"
          },
          {
            key: "invoice_date",
            label: "Date / Due",
            flex: 1.1,
            render: (r) => (
              <View>
                <Text style={{ fontSize: 12, color: colors.text }}>{r.invoice_date || "-"}</Text>
                <Text style={{ fontSize: 11, color: colors.muted }}>Due: {r.due_date || "-"}</Text>
              </View>
            )
          },
          {
            key: "grand_total",
            label: "Grand Total",
            flex: 1.2,
            render: (r) => formatCurrency(r.grand_total)
          },
          {
            key: "amount_paid",
            label: "Paid / Due",
            flex: 1.2,
            render: (r) => (
              <View>
                <Text style={{ fontSize: 12, color: colors.primary, fontWeight: "600" }}>
                  {formatCurrency(r.amount_paid)}
                </Text>
                <Text
                  style={{
                    fontSize: 11,
                    color: Number(r.outstanding_amount || 0) > 0 ? colors.danger : colors.muted
                  }}
                >
                  Due: {formatCurrency(r.outstanding_amount)}
                </Text>
              </View>
            )
          },
          {
            key: "status",
            label: "Status",
            flex: 0.9,
            render: (r) => <InvoiceStatusBadge status={r.status} />
          }
        ]}
        rows={invoices}
        renderActions={(row) => {
          const st = String(row.status || "").toLowerCase();
          const isDraft = st === "draft";
          const isCancelled = st === "cancelled";
          const isPaid = st === "paid";
          const hasBalance = Number(row.outstanding_amount || 0) > 0;
          const isOwner = Number(row.created_by_user_id) === Number(user?.id);

          return (
            <ActionMenu
              actions={[
                { label: "View Details", icon: Eye, onPress: () => openInvoiceDetail(row.id) },
                isDraft && (isAdmin || isOwner) && { label: "Issue Officially", icon: CheckCircle, variant: "primary", onPress: () => handleIssueInvoice(row.id) },
                (isAdmin || (isDraft && isOwner)) && { label: isDraft ? "Edit Draft Invoice" : "Edit Invoice", icon: Edit3, onPress: () => openEditDraftModal(row) },
                !isDraft && !isCancelled && hasBalance && (isAdmin || isOwner) && { label: "Record Payment", icon: CreditCard, variant: "success", onPress: () => openPaymentModal(row) },
                isDraft && { label: "Download Draft PDF", icon: FileText, onPress: () => downloadPdf(row.id, "draft") },
                !isDraft && { label: "Download Official PDF", icon: Download, variant: "primary", onPress: () => downloadPdf(row.id, "final") },
                !isDraft && !isCancelled && !isPaid && (isAdmin || isOwner) && { label: "Cancel Invoice", icon: XCircle, variant: "danger", onPress: () => openCancelModal(row) },
                (isAdmin || (isDraft && isOwner)) && { label: isDraft ? "Delete Draft Invoice" : "Delete Invoice", icon: Trash2, variant: "danger", onPress: () => handleDeleteInvoice(row.id) }
              ]}
            />
          );
        }}
      />

      {/* INVOICE DETAIL MODAL / DRAWER */}
      {selectedInvoice ? (
        <Modal
          visible={Boolean(selectedInvoice)}
          title={`Invoice ${selectedInvoice.invoice_number}`}
          onClose={() => setSelectedInvoice(null)}
        >
          <ScrollView style={{ maxHeight: 600, gap: 14 }}>
            {/* Top Info Banner */}
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "flex-start",
                backgroundColor: colors.secondaryBg,
                padding: 12,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: colors.border
              }}
            >
              <View style={{ gap: 4 }}>
                <Text style={{ fontSize: 15, fontWeight: "700", color: colors.text }}>
                  {selectedInvoice.client_name}
                </Text>
                <Text style={{ fontSize: 12, color: colors.muted }}>
                  Ref Quotation: {selectedInvoice.quotation_number}
                </Text>
                {selectedInvoice.project_title ? (
                  <Text style={{ fontSize: 12, color: colors.text }}>
                    Project: {selectedInvoice.project_title}
                  </Text>
                ) : null}
                <Text style={{ fontSize: 11, color: colors.subtle }}>
                  Invoice Date: {selectedInvoice.invoice_date} | Due Date: {selectedInvoice.due_date || "-"}
                </Text>
              </View>
              <View style={{ gap: 6, alignItems: "flex-end" }}>
                <InvoiceStatusBadge status={selectedInvoice.status} />
                <InvoiceTypeBadge type={selectedInvoice.invoice_type} rate={selectedInvoice.tax_rate} />
              </View>
            </View>

            {/* Financial Summary */}
            <View
              style={{
                flexDirection: "row",
                gap: 10,
                flexWrap: "wrap",
                backgroundColor: colors.surface,
                padding: 12,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: colors.border
              }}
            >
              <View style={{ flex: 1, minWidth: 100 }}>
                <Text style={styles.small}>Subtotal</Text>
                <Text style={{ fontSize: 15, fontWeight: "600", color: colors.text }}>
                  {formatCurrency(selectedInvoice.subtotal)}
                </Text>
              </View>
              {selectedInvoice.invoice_type === "sales_tax" || Number(selectedInvoice.tax_total || 0) > 0 ? (
                <View style={{ flex: 1, minWidth: 110 }}>
                  <Text style={styles.small}>Sales Tax ({selectedInvoice.tax_rate || 18}%)</Text>
                  <Text style={{ fontSize: 15, fontWeight: "600", color: colors.info }}>
                    {formatCurrency(selectedInvoice.tax_total)}
                  </Text>
                </View>
              ) : null}
              {Number(selectedInvoice.extra_charges || 0) > 0 ? (
                <View style={{ flex: 1, minWidth: 100 }}>
                  <Text style={styles.small}>Extra Charges</Text>
                  <Text style={{ fontSize: 15, fontWeight: "600", color: colors.text }}>
                    {formatCurrency(selectedInvoice.extra_charges)}
                  </Text>
                </View>
              ) : null}
              <View style={{ flex: 1, minWidth: 110 }}>
                <Text style={styles.small}>Grand Total</Text>
                <Text style={{ fontSize: 16, fontWeight: "700", color: colors.text }}>
                  {formatCurrency(selectedInvoice.grand_total)}
                </Text>
              </View>
              <View style={{ flex: 1, minWidth: 110 }}>
                <Text style={styles.small}>Amount Paid</Text>
                <Text style={{ fontSize: 16, fontWeight: "700", color: colors.primary }}>
                  {formatCurrency(selectedInvoice.amount_paid)}
                </Text>
              </View>
              <View style={{ flex: 1, minWidth: 110 }}>
                <Text style={styles.small}>Outstanding</Text>
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: "700",
                    color: Number(selectedInvoice.outstanding_amount || 0) > 0 ? colors.danger : colors.muted
                  }}
                >
                  {formatCurrency(selectedInvoice.outstanding_amount)}
                </Text>
              </View>
            </View>

            {/* Invoiced Items Table */}
            <View style={{ gap: 6 }}>
              <Text style={{ fontSize: 13, fontWeight: "700", color: colors.text }}>
                Line Items ({(selectedInvoice.items || []).length})
              </Text>
              <View style={{ borderWidth: 1, borderColor: colors.border, borderRadius: 6, overflow: "hidden" }}>
                <View
                  style={{
                    flexDirection: "row",
                    backgroundColor: colors.headerBg,
                    padding: 8,
                    borderBottomWidth: 1,
                    borderBottomColor: colors.border
                  }}
                >
                  <Text style={{ flex: 2, fontSize: 11, fontWeight: "700" }}>Item</Text>
                  <Text style={{ width: 50, fontSize: 11, fontWeight: "700", textAlign: "center" }}>Unit</Text>
                  <Text style={{ width: 45, fontSize: 11, fontWeight: "700", textAlign: "center" }}>Qty</Text>
                  <Text style={{ width: 75, fontSize: 11, fontWeight: "700", textAlign: "right" }}>Price</Text>
                  {selectedInvoice.invoice_type === "sales_tax" ? (
                    <Text style={{ width: 70, fontSize: 11, fontWeight: "700", textAlign: "right" }}>Tax</Text>
                  ) : null}
                  <Text style={{ width: 85, fontSize: 11, fontWeight: "700", textAlign: "right" }}>Total</Text>
                </View>
                {(selectedInvoice.items || []).map((it, idx) => (
                  <View
                    key={it.id || idx}
                    style={{
                      flexDirection: "row",
                      padding: 8,
                      borderBottomWidth: idx === selectedInvoice.items.length - 1 ? 0 : 1,
                      borderBottomColor: colors.border,
                      backgroundColor: idx % 2 === 1 ? colors.secondaryBg : colors.surface
                    }}
                  >
                    <View style={{ flex: 2 }}>
                      <Text style={{ fontSize: 12, fontWeight: "600", color: colors.text }}>{it.item_name}</Text>
                      {it.description ? (
                        <Text style={{ fontSize: 10, color: colors.muted }} numberOfLines={1}>
                          {it.description}
                        </Text>
                      ) : null}
                    </View>
                    <Text style={{ width: 50, fontSize: 11, textAlign: "center", color: colors.muted }}>
                      {it.unit || "Nos"}
                    </Text>
                    <Text style={{ width: 45, fontSize: 11, textAlign: "center", fontWeight: "600" }}>
                      {it.quantity}
                    </Text>
                    <Text style={{ width: 75, fontSize: 11, textAlign: "right" }}>
                      {formatAmount(it.unit_price)}
                    </Text>
                    {selectedInvoice.invoice_type === "sales_tax" ? (
                      <Text style={{ width: 70, fontSize: 11, textAlign: "right", color: colors.info }}>
                        {formatAmount(it.tax)}
                      </Text>
                    ) : null}
                    <Text style={{ width: 85, fontSize: 11, textAlign: "right", fontWeight: "600" }}>
                      {formatAmount(it.line_total)}
                    </Text>
                  </View>
                ))}
              </View>
            </View>

            {/* Payment History */}
            <View style={{ gap: 6 }}>
              <View style={styles.between}>
                <Text style={{ fontSize: 13, fontWeight: "700", color: colors.text }}>
                  Recorded Payments ({(selectedInvoice.payments || []).length})
                </Text>
                {canManage &&
                String(selectedInvoice.status).toLowerCase() !== "draft" &&
                String(selectedInvoice.status).toLowerCase() !== "cancelled" &&
                Number(selectedInvoice.outstanding_amount || 0) > 0 ? (
                  <Button
                    icon={CreditCard}
                    onPress={() => openPaymentModal(selectedInvoice)}
                    style={{ height: 28, paddingHorizontal: 8, fontSize: 11 }}
                  >
                    Record Payment
                  </Button>
                ) : null}
              </View>

              {(selectedInvoice.payments || []).length > 0 ? (
                <View style={{ borderWidth: 1, borderColor: colors.border, borderRadius: 6, overflow: "hidden" }}>
                  <View
                    style={{
                      flexDirection: "row",
                      backgroundColor: colors.headerBg,
                      padding: 8,
                      borderBottomWidth: 1,
                      borderBottomColor: colors.border
                    }}
                  >
                    <Text style={{ width: 90, fontSize: 11, fontWeight: "700" }}>Date</Text>
                    <Text style={{ width: 90, fontSize: 11, fontWeight: "700" }}>Method</Text>
                    <Text style={{ width: 100, fontSize: 11, fontWeight: "700" }}>Reference</Text>
                    <Text style={{ flex: 1, fontSize: 11, fontWeight: "700" }}>Recorded By / Notes</Text>
                    <Text style={{ width: 90, fontSize: 11, fontWeight: "700", textAlign: "right" }}>Amount</Text>
                  </View>
                  {selectedInvoice.payments.map((p, pidx) => (
                    <View
                      key={p.id || pidx}
                      style={{
                        flexDirection: "row",
                        padding: 8,
                        borderBottomWidth: pidx === selectedInvoice.payments.length - 1 ? 0 : 1,
                        borderBottomColor: colors.border,
                        backgroundColor: pidx % 2 === 1 ? colors.secondaryBg : colors.surface
                      }}
                    >
                      <Text style={{ width: 90, fontSize: 11 }}>{p.payment_date}</Text>
                      <Text style={{ width: 90, fontSize: 11, fontWeight: "600" }}>{p.payment_method}</Text>
                      <Text style={{ width: 100, fontSize: 11, color: colors.muted }}>
                        {p.transaction_reference || "-"}
                      </Text>
                      <Text style={{ flex: 1, fontSize: 11, color: colors.muted }}>
                        {p.recorded_by_name ? `By: ${p.recorded_by_name}` : ""}{" "}
                        {p.notes ? `(${p.notes})` : ""}
                      </Text>
                      <Text style={{ width: 90, fontSize: 11, textAlign: "right", fontWeight: "700", color: colors.primary }}>
                        {formatCurrency(p.amount)}
                      </Text>
                    </View>
                  ))}
                </View>
              ) : (
                <View
                  style={{
                    padding: 14,
                    backgroundColor: colors.secondaryBg,
                    borderRadius: 6,
                    alignItems: "center"
                  }}
                >
                  <Text style={[styles.small, { color: colors.muted }]}>
                    No payments have been recorded for this invoice yet.
                  </Text>
                </View>
              )}
            </View>

            {/* Terms and Remarks */}
            {selectedInvoice.terms || selectedInvoice.remarks ? (
              <View style={{ gap: 8, backgroundColor: colors.secondaryBg, padding: 12, borderRadius: 6 }}>
                {selectedInvoice.terms ? (
                  <View style={{ gap: 2 }}>
                    <Text style={{ fontSize: 11, fontWeight: "700", color: colors.text }}>Terms & Conditions:</Text>
                    <Text style={{ fontSize: 11, color: colors.muted }}>{selectedInvoice.terms}</Text>
                  </View>
                ) : null}
                {selectedInvoice.remarks ? (
                  <View style={{ gap: 2 }}>
                    <Text style={{ fontSize: 11, fontWeight: "700", color: colors.text }}>Remarks / Notes:</Text>
                    <Text style={{ fontSize: 11, color: colors.muted }}>{selectedInvoice.remarks}</Text>
                  </View>
                ) : null}
              </View>
            ) : null}

            {/* Modal Bottom Actions */}
            <View style={{ flexDirection: "row", gap: 8, justifyContent: "flex-end", marginTop: 12, flexWrap: "wrap" }}>
              {String(selectedInvoice.status).toLowerCase() === "draft" ? (
                <>
                  {canManage ? (
                    <Button icon={CheckCircle} onPress={() => handleIssueInvoice(selectedInvoice.id)}>
                      Issue Invoice
                    </Button>
                  ) : null}
                  <Button
                    icon={Download}
                    variant="secondary"
                    onPress={() => downloadPdf(selectedInvoice.id, "draft")}
                  >
                    Draft PDF
                  </Button>
                </>
              ) : (
                <Button
                  icon={Download}
                  variant="secondary"
                  onPress={() => downloadPdf(selectedInvoice.id, "final")}
                >
                  Download Final PDF
                </Button>
              )}
            </View>
          </ScrollView>
        </Modal>
      ) : null}

      {/* CREATE INVOICE MODAL */}
      <Modal
        visible={showCreateModal}
        title="Generate Invoice from Approved Quotation"
        onClose={() => setShowCreateModal(false)}
      >
        <ScrollView style={{ maxHeight: 520, gap: 14 }}>
          <Text style={[styles.body, { color: colors.muted }]}>
            Select an approved quotation to convert into a formal tax invoice. Only approved quotations without an existing invoice can be selected.
          </Text>

          {loadingQuotations ? (
            <Text style={styles.body}>Loading approved quotations...</Text>
          ) : (
            <Select
              label="Select Approved Quotation"
              value={selectedQuotationId}
              onChangeText={(val) => {
                setSelectedQuotationId(val);
                const match = approvedQuotations.find((q) => String(q.id) === String(val));
                if (match) {
                  setCreateForm((prev) => ({
                    ...prev,
                    payment_terms: match.terms || prev.payment_terms || "30 Days Net",
                    remarks: match.remarks || prev.remarks
                  }));
                }
              }}
            >
              <option value="">-- Choose Approved Quotation --</option>
              {approvedQuotations.map((q) => {
                const hasInv = Boolean(q.invoice_id);
                return (
                  <option key={q.id} value={q.id} disabled={hasInv}>
                    {q.quotation_number} - {q.client_name} (PKR {formatAmount(q.grand_total)}){" "}
                    {hasInv ? `[Invoiced: ${q.invoice_number}]` : ""}
                  </option>
                );
              })}
            </Select>
          )}

          {/* Invoice Type & Tax Setting */}
          {(() => {
            const selectedQ = approvedQuotations.find((q) => String(q.id) === String(selectedQuotationId));
            const subtotal = Number(selectedQ?.subtotal ?? selectedQ?.grand_total ?? 0);
            const discount = Number(selectedQ?.discount_total || 0);
            const netBase = Math.max(0, subtotal - discount);
            const extra = Number(selectedQ?.extra_charges || 0);
            const isTax = createForm.invoice_type === "sales_tax";
            const rate = isTax ? Math.max(0, Number(createForm.tax_rate || 0)) : 0;
            const taxAmt = isTax ? Math.round((netBase * (rate / 100)) * 100) / 100 : 0;
            const grandTotal = netBase + taxAmt + extra;

            return (
              <View
                style={{
                  gap: 8,
                  backgroundColor: colors.secondaryBg,
                  padding: 12,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: colors.border
                }}
              >
                <Text style={{ fontSize: 13, fontWeight: "700", color: colors.text }}>
                  Invoice Type & Taxation Setting
                </Text>
                <View style={styles.row}>
                  <Select
                    label="Invoice Type"
                    value={createForm.invoice_type}
                    onChangeText={(v) => setCreateForm({ ...createForm, invoice_type: v })}
                    style={{ flex: 2 }}
                  >
                    <option value="commercial">Commercial Invoice (Without Sales Tax - 0%)</option>
                    <option value="sales_tax">Sales Tax Invoice (With Sales Tax %)</option>
                  </Select>
                  {createForm.invoice_type === "sales_tax" ? (
                    <Field
                      label="Sales Tax Rate (%)"
                      value={createForm.tax_rate}
                      onChangeText={(v) => setCreateForm({ ...createForm, tax_rate: v })}
                      placeholder="18"
                      keyboardType="numeric"
                      style={{ flex: 1 }}
                    />
                  ) : null}
                </View>

                {selectedQ ? (
                  <View
                    style={{
                      marginTop: 6,
                      paddingTop: 8,
                      borderTopWidth: 1,
                      borderTopColor: colors.border,
                      gap: 4
                    }}
                  >
                    <View style={styles.between}>
                      <Text style={styles.small}>Base Amount (Excl. Tax):</Text>
                      <Text style={[styles.small, { fontWeight: "600" }]}>{formatCurrency(netBase)}</Text>
                    </View>
                    {isTax ? (
                      <View style={styles.between}>
                        <Text style={[styles.small, { color: colors.info }]}>
                          Sales Tax ({rate}%):
                        </Text>
                        <Text style={[styles.small, { fontWeight: "700", color: colors.info }]}>
                          +{formatCurrency(taxAmt)}
                        </Text>
                      </View>
                    ) : (
                      <View style={styles.between}>
                        <Text style={styles.small}>Sales Tax (Commercial / Exempt):</Text>
                        <Text style={[styles.small, { color: colors.muted }]}>PKR 0.00 (0%)</Text>
                      </View>
                    )}
                    {extra > 0 ? (
                      <View style={styles.between}>
                        <Text style={styles.small}>Extra Charges:</Text>
                        <Text style={[styles.small, { fontWeight: "600" }]}>+{formatCurrency(extra)}</Text>
                      </View>
                    ) : null}
                    <View
                      style={[
                        styles.between,
                        {
                          marginTop: 4,
                          paddingTop: 4,
                          borderTopWidth: 1,
                          borderTopColor: colors.border
                        }
                      ]}
                    >
                      <Text style={{ fontSize: 13, fontWeight: "700", color: colors.text }}>
                        Estimated Invoice Total:
                      </Text>
                      <Text style={{ fontSize: 15, fontWeight: "700", color: colors.primary }}>
                        {formatCurrency(grandTotal)}
                      </Text>
                    </View>
                  </View>
                ) : null}
              </View>
            );
          })()}

          <View style={styles.row}>
            <Field
              label="Invoice Date"
              value={createForm.invoice_date}
              onChangeText={(v) => setCreateForm({ ...createForm, invoice_date: v })}
              placeholder="YYYY-MM-DD"
            />
            <Field
              label="Due Date (optional)"
              value={createForm.due_date}
              onChangeText={(v) => setCreateForm({ ...createForm, due_date: v })}
              placeholder="YYYY-MM-DD"
            />
          </View>

          <Field
            label="Payment Terms"
            value={createForm.payment_terms}
            onChangeText={(v) => setCreateForm({ ...createForm, payment_terms: v })}
            placeholder="e.g. 30 Days Net / Advance / 50% on Delivery"
          />

          <Field
            label="Remarks / Notes (optional)"
            value={createForm.remarks}
            onChangeText={(v) => setCreateForm({ ...createForm, remarks: v })}
            multiline
            placeholder="Commercial or delivery remarks..."
          />

          <View style={{ flexDirection: "row", gap: 10, justifyContent: "flex-end", marginTop: 10 }}>
            <Button variant="secondary" onPress={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              icon={Receipt}
              onPress={handleCreateInvoice}
              disabled={!selectedQuotationId || creatingInvoice}
              loading={creatingInvoice}
            >
              Generate Invoice
            </Button>
          </View>
        </ScrollView>
      </Modal>

      {/* RECORD PAYMENT MODAL */}
      {paymentTargetInvoice ? (
        <Modal
          visible={showPaymentModal}
          title={`Record Payment for ${paymentTargetInvoice.invoice_number}`}
          onClose={() => setShowPaymentModal(false)}
        >
          <ScrollView style={{ maxHeight: 520, gap: 14 }}>
            <View
              style={{
                backgroundColor: colors.secondaryBg,
                padding: 12,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: colors.border
              }}
            >
              <Text style={{ fontSize: 13, fontWeight: "600", color: colors.text }}>
                Client: {paymentTargetInvoice.client_name}
              </Text>
              <Text style={{ fontSize: 12, color: colors.muted, marginTop: 2 }}>
                Invoice Grand Total: {formatCurrency(paymentTargetInvoice.grand_total)}
              </Text>
              <Text style={{ fontSize: 13, fontWeight: "700", color: colors.danger, marginTop: 4 }}>
                Current Outstanding Balance: {formatCurrency(paymentTargetInvoice.outstanding_amount)}
              </Text>
            </View>

            <View style={styles.row}>
              <Field
                label="Payment Amount (PKR)"
                value={paymentForm.amount}
                onChangeText={(v) => setPaymentForm({ ...paymentForm, amount: v })}
                placeholder="Amount in PKR"
                keyboardType="numeric"
              />
              <Field
                label="Payment Date"
                value={paymentForm.payment_date}
                onChangeText={(v) => setPaymentForm({ ...paymentForm, payment_date: v })}
                placeholder="YYYY-MM-DD"
              />
            </View>

            <Select
              label="Payment Method"
              value={paymentForm.payment_method}
              onChangeText={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>

            <Field
              label="Transaction Ref / Cheque No"
              value={paymentForm.transaction_reference}
              onChangeText={(v) => setPaymentForm({ ...paymentForm, transaction_reference: v })}
              placeholder="e.g. TR-98234 or Chq #00123"
            />

            <Field
              label="Bank Details"
              value={paymentForm.bank_details}
              onChangeText={(v) => setPaymentForm({ ...paymentForm, bank_details: v })}
              placeholder="e.g. HBL Main Branch A/C 123456"
            />

            <Field
              label="Notes"
              value={paymentForm.notes}
              onChangeText={(v) => setPaymentForm({ ...paymentForm, notes: v })}
              placeholder="Payment remarks or deposit confirmation"
            />

            <View style={{ flexDirection: "row", gap: 10, justifyContent: "flex-end", marginTop: 10 }}>
              <Button variant="secondary" onPress={() => setShowPaymentModal(false)}>
                Cancel
              </Button>
              <Button
                icon={CreditCard}
                onPress={handleRecordPayment}
                loading={paymentSubmitting}
              >
                Save Payment
              </Button>
            </View>
          </ScrollView>
        </Modal>
      ) : null}

      {/* CANCEL INVOICE MODAL */}
      {cancelTargetInvoice ? (
        <Modal
          visible={showCancelModal}
          title={`Cancel Invoice ${cancelTargetInvoice.invoice_number}`}
          onClose={() => setShowCancelModal(false)}
        >
          <View style={{ gap: 14 }}>
            <Text style={[styles.body, { color: colors.danger }]}>
              Warning: Cancelling this invoice will invalidate it and halt payment tracking. This action cannot be undone.
            </Text>

            <Field
              label="Cancellation Reason"
              value={cancelReason}
              onChangeText={setCancelReason}
              placeholder="State the reason for voiding/cancelling this invoice..."
              multiline
            />

            <View style={{ flexDirection: "row", gap: 10, justifyContent: "flex-end", marginTop: 10 }}>
              <Button variant="secondary" onPress={() => setShowCancelModal(false)}>
                Go Back
              </Button>
              <Button variant="danger" icon={XCircle} onPress={handleCancelInvoice}>
                Confirm Cancellation
              </Button>
            </View>
          </View>
        </Modal>
      ) : null}

      {/* EDIT DRAFT INVOICE MODAL */}
      {editingDraft ? (
        <Modal
          visible={showEditDraftModal}
          title={`Edit Draft Invoice ${editingDraft.invoice_number}`}
          onClose={() => setShowEditDraftModal(false)}
        >
          <ScrollView style={{ maxHeight: 540, gap: 14 }}>
            {/* Invoice Type & Tax Setting */}
            <View
              style={{
                gap: 8,
                backgroundColor: colors.secondaryBg,
                padding: 12,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: colors.border
              }}
            >
              <Text style={{ fontSize: 13, fontWeight: "700", color: colors.text }}>
                Invoice Type & Taxation Setting
              </Text>
              <View style={styles.row}>
                <Select
                  label="Invoice Type"
                  value={editingDraft.invoice_type || "commercial"}
                  onChangeText={(v) => setEditingDraft({ ...editingDraft, invoice_type: v })}
                  style={{ flex: 2 }}
                >
                  <option value="commercial">Commercial Invoice (Without Sales Tax - 0%)</option>
                  <option value="sales_tax">Sales Tax Invoice (With Sales Tax %)</option>
                </Select>
                {editingDraft.invoice_type === "sales_tax" ? (
                  <Field
                    label="Sales Tax Rate (%)"
                    value={editingDraft.tax_rate}
                    onChangeText={(v) => setEditingDraft({ ...editingDraft, tax_rate: v })}
                    placeholder="18"
                    keyboardType="numeric"
                    style={{ flex: 1 }}
                  />
                ) : null}
              </View>
              <Text style={[styles.small, { color: colors.muted }]}>
                {editingDraft.invoice_type === "sales_tax"
                  ? `Saving will automatically calculate sales tax at ${editingDraft.tax_rate || 18}% on all line items.`
                  : "Commercial invoice will set tax to PKR 0 (0%) on all line items."}
              </Text>
            </View>

            <View style={styles.row}>
              <Field
                label="Invoice Date"
                value={editingDraft.invoice_date}
                onChangeText={(v) => setEditingDraft({ ...editingDraft, invoice_date: v })}
                placeholder="YYYY-MM-DD"
              />
              <Field
                label="Due Date"
                value={editingDraft.due_date}
                onChangeText={(v) => setEditingDraft({ ...editingDraft, due_date: v })}
                placeholder="YYYY-MM-DD"
              />
            </View>

            <View style={styles.row}>
              <Field
                label="Payment Terms"
                value={editingDraft.payment_terms}
                onChangeText={(v) => setEditingDraft({ ...editingDraft, payment_terms: v })}
                placeholder="e.g. 30 Days Net"
              />
              <Field
                label="Extra Charges (PKR)"
                value={String(editingDraft.extra_charges || 0)}
                onChangeText={(v) => setEditingDraft({ ...editingDraft, extra_charges: v })}
                keyboardType="numeric"
              />
            </View>

            <Field
              label="Project Title"
              value={editingDraft.project_title}
              onChangeText={(v) => setEditingDraft({ ...editingDraft, project_title: v })}
              placeholder="Project title..."
            />

            <Field
              label="Commercial Remarks"
              value={editingDraft.remarks}
              onChangeText={(v) => setEditingDraft({ ...editingDraft, remarks: v })}
              multiline
            />

            <Field
              label="Terms & Conditions"
              value={editingDraft.terms}
              onChangeText={(v) => setEditingDraft({ ...editingDraft, terms: v })}
              multiline
            />

            <View style={{ flexDirection: "row", gap: 10, justifyContent: "flex-end", marginTop: 10 }}>
              <Button variant="secondary" onPress={() => setShowEditDraftModal(false)}>
                Cancel
              </Button>
              <Button icon={CheckCircle} onPress={handleSaveDraftChanges}>
                Save Changes
              </Button>
            </View>
          </ScrollView>
        </Modal>
      ) : null}
    </View>
  );
}

function InvoiceStatusBadge({ status }) {
  const st = String(status || "draft").toLowerCase();
  let variant = "draft";
  let label = "Draft";

  if (st === "issued") {
    variant = "info";
    label = "Issued";
  } else if (st === "partially_paid") {
    variant = "warning";
    label = "Partially Paid";
  } else if (st === "paid") {
    variant = "approved";
    label = "Paid";
  } else if (st === "overdue") {
    variant = "danger";
    label = "Overdue";
  } else if (st === "cancelled") {
    variant = "rejected";
    label = "Cancelled";
  }

  return <Badge variant={variant}>{label}</Badge>;
}

function InvoiceTypeBadge({ type, rate }) {
  const isTax = String(type || "").toLowerCase() === "sales_tax";
  if (isTax) {
    const r = rate != null && rate !== "" ? rate : 18;
    return <Badge variant="info">{`Sales Tax (${r}%)`}</Badge>;
  }
  return <Badge variant="secondary">Commercial</Badge>;
}
